import {
  LogInPaths,
  REMOVED_SELF_SERVE_PATHS,
} from '@bbkAdminComponents/log-in-flow/log-in-paths';
import {
  type ActiveApp,
  type ActiveCompany,
  type ActiveUser,
} from '@bbkAdminRedux/app/reducers';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { matchPath } from '@bbkShared/react-router';

/**
 * The company/app a signed-in user was last working in, already validated as
 * belonging to them. Ownership is decided by `selectRememberedActiveContext`,
 * the only place that can see both the per-tab and the per-browser tier.
 */
export type RememberedContext = Partial<{
  companyKey: string;
  appKey: string;
}>;

const findCompany = (user: ActiveUser, companyKey?: string) => {
  if (!companyKey) return undefined;
  return user.companies.find((c) => c.internal_name === companyKey);
};
const findApp = (company: ActiveCompany, appKey?: string) => {
  if (!appKey) return undefined;
  if (appKey === '_') {
    return company.applications[0];
  }
  return company.applications.find((a) => a.encoded_id === appKey);
};

export type ActiveContext = {
  company?: ActiveCompany;
  app?: ActiveApp;
  redirect?: string;
};

const isPathOrDescendant = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const isUnscopedPath = (pathname: string) =>
  [
    LogInPaths.selector,
    LogInPaths.invite,
    LogInPaths.logout,
    LogInPaths.cbidpExchange,
    ...REMOVED_SELF_SERVE_PATHS,
  ].some((path) => isPathOrDescendant(pathname, path));

const isInternalPath = (pathname: string) =>
  isPathOrDescendant(pathname, '/internal');

const isCompanyLandingPath = (pathname: string) =>
  pathname === LogInPaths.company ||
  pathname === `${LogInPaths.company}/` ||
  pathname === LogInPaths.signIn ||
  pathname === `${LogInPaths.signIn}/`;

const resolveDefaultApp = (
  company: ActiveCompany,
  remembered?: RememberedContext
) =>
  remembered?.companyKey === company.internal_name
    ? (findApp(company, remembered.appKey) ?? company.applications[0])
    : company.applications[0];

export const resolveActiveContext = (
  user: ActiveUser,
  pathname: string,
  remembered?: RememberedContext
): ActiveContext => {
  if (isUnscopedPath(pathname)) return {};

  // Internal pages are cross-tenant, but the nav and the user feature flags
  // still need *a* tenant, so resume one instead of clearing the context.
  // Never redirect: a deep-linked internal admin must stay where they are.
  if (isInternalPath(pathname)) {
    const candidates = [
      findCompany(user, remembered?.companyKey),
      findCompany(user, user.selected_company),
      ...user.companies,
    ].filter(isDefined);
    // AuthRoot renders <NoApps /> for a company with no applications, on every
    // path, so an app-less candidate locks the user out of internal tooling
    // just as thoroughly as the blank page this resolution exists to fix. When
    // every candidate is app-less there is nothing safe to resume, so leave the
    // context empty rather than resolving one.
    const company = candidates.find((c) => c.applications.length > 0);
    return company
      ? { company, app: resolveDefaultApp(company, remembered) }
      : {};
  }

  if (!pathname.startsWith('/company')) return {};

  const appMatch = matchPath<{ companyKey: string; appKey: string }>(pathname, {
    path: '/company/:companyKey/app/:appKey',
  });
  if (appMatch) {
    const company = findCompany(user, appMatch.params.companyKey);
    if (!company) return { redirect: LogInPaths.company };

    const app = findApp(company, appMatch.params.appKey);
    if (app) return { company, app };

    return {
      company,
      app: resolveDefaultApp(company, remembered),
      redirect: `${LogInPaths.company}/${company.internal_name}`,
    };
  }

  const companyMatch = matchPath<{ companyKey: string }>(pathname, {
    path: '/company/:companyKey',
  });
  if (companyMatch && !isCompanyLandingPath(pathname)) {
    const company = findCompany(user, companyMatch.params.companyKey);
    return company
      ? { company, app: resolveDefaultApp(company, remembered) }
      : { redirect: LogInPaths.company };
  }

  if (!isCompanyLandingPath(pathname)) return {};

  const company =
    findCompany(user, user.selected_company) ??
    findCompany(user, remembered?.companyKey) ??
    (user.companies.length === 1 ? user.companies[0] : undefined);
  if (!company) return {};

  return {
    company,
    app: resolveDefaultApp(company, remembered),
  };
};
